import { Directive, ElementRef, Input, AfterViewInit, OnInit, Renderer2, OnDestroy, EventEmitter, Inject, Optional, PLATFORM_ID, HostBinding, booleanAttribute, numberAttribute } from '@angular/core';
import { debounceTime, throttle } from 'rxjs/operators';
import { interval, timer, Subscription, combineLatest, of, fromEvent } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { wowAssetsDefaultConfig, WOW_ASSETS } from './wow.module';
import { EnumScrollType, WowSetting } from './wow-setting';
import { NgwWowConfig } from './wow-config';
import { loadLazyCssFile, loadLazyJsFile } from '@techmore/global';

/**
 * Type definition for the `WOW` object from `WOW.js` library.
 */
declare class WOW {
  constructor(config?: NgwWowConfig);
  /**
   * Initializes the WOW.js library
   * @param config the custom configuration to use
   */
  init(config?: NgwWowConfig): void;
  boxes: any[];
}

@Directive({
  selector: '[tmWow]',
  exportAs: 'tmWow',
})
export class TmWowDirective implements OnInit, AfterViewInit, OnDestroy {
  static count = 0;
  /** wow 插件初始化完成後觸發 */
  static pulginInit = new EventEmitter<void>();
  /** 全域的 wow 插件初始化完成 */
  static isPluginInit = false;
  /** 全域的 wow 插件在正初始化中 */
  static isPluginIniting = false;
  /** 載入插件的方法 */
  static loadPlugin(document: Document, renderer: Renderer2, parentEl: Element, assetsConfig: { scripts: string[]; styles: string[]; }) {
    TmWowDirective.isPluginIniting = true;
    assetsConfig.scripts.forEach(sc => {
      const sca = sc.split('/');
      const scName = sca[sca.length - 1];
      loadLazyJsFile(document, renderer, scName, sc);
    });
    assetsConfig.styles.forEach(st => {
      const sta = st.split('/');
      const stName = sta[sta.length - 1];
      loadLazyCssFile(document, renderer, parentEl, stName, st);
    });
    checkPluginCanUse(() => {
      TmWowDirective.isPluginInit = true;
      TmWowDirective.isPluginIniting = false;
      TmWowDirective.pulginInit.emit();
      TmWowDirective.pulginInit.complete();
    });
  }
  /** 是否渲染端為瀏覽器 */
  protected isBrowser: boolean;
  /** 是否可以開始使用元件了 */
  canInit = false;
  /** 取得綁定的元素DOM */
  el: HTMLElement;
  /** 運用特效 */
  private _config: WowSetting = {
    turnOn: false,
    animate: 'fadeInUp',
  };
  /** 運用特效 */
  @Input('tmWow') set config(config: WowSetting) {
    if (config) {
      this._config = config;
    }
  }
  get config() { return this._config; }
  /** 應用特效時機，預設為向下滾動 */
  @Input() applyScroll = EnumScrollType.Down;
  /** 是否重複特效，預設為否 */
  @Input({transform: booleanAttribute}) replay = false;
  /** Change the animation duration */
  @Input({transform: numberAttribute}) duration?: number;
  /** Distance to start the animation (related to the browser bottom) */
  @Input({transform: numberAttribute}) offset?: number;
  /** Delay before the animation starts */
  @Input({transform: numberAttribute}) delay?: number;
  /** Number of times the animation is repeated */
  @Input({transform: numberAttribute}) iteration?: number;
  /** 是否自動做初始化，預設為真 */
  @Input() autoInit = true;
  /**
   * 是否強制啟用wow效果，不等圖片載入完成就套用，預設為否。
   * 
   * 用在carousel上，因裡面圖片有N個且可能未載入完成，會使wow效果套用陷入等待，
   * 可以再圖片即使未載入完但有設定好寬高比不影響高度計算的情況強制做wow效果套用
   */
  @Input() forceInitWow = false;
  /** wow 動畫是否播放結束 */
  animateEnd = false;
 
  private scrollSubc?: Subscription;
  private _subcs: Subscription[] = [];
  private instance?: WOW;
  private genCount: number;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(WOW_ASSETS) private assets: any,
    private elRef: ElementRef,
    private render: Renderer2,
    @Inject(DOCUMENT) private document: any,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.el = this.elRef.nativeElement;
    this.genCount = TmWowDirective.count++;
    this.render.addClass(this.el, `wow-gen-${this.genCount}`);
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      if (!TmWowDirective.isPluginInit && !TmWowDirective.isPluginIniting) {
        if (isPluginLoaded()) {
          TmWowDirective.isPluginInit = true;
        } else {
          TmWowDirective.loadPlugin(this.document, this.render, this.elRef.nativeElement, this.assets || wowAssetsDefaultConfig);
        }
      }

      if (!TmWowDirective.isPluginInit) {
        TmWowDirective.pulginInit.subscribe(() => {
          if (this.autoInit) {
            this.init();
          }
          this.canInit = true;
        });
      } else {
        if (this.autoInit) {
          this.init();
        }
        this.canInit = true;
      }
    }
  }

  ngOnDestroy() {
    if (this.scrollSubc) {
      this.scrollSubc.unsubscribe();
    }
    while (this._subcs.length) {
      this._subcs.pop()?.unsubscribe();
    }
  }

  init() {
    /*
     * 在viewInit後開始抓取元素
     * 判斷如果自己本身為IMG
     * 或子層元素為IMG
     * 如果自身為IMG，監聽自己的load事件，完成後開始計算宿主元素高度
     * 如果子層元素有IMG，監聽它的load事件，完成後開始計算宿主元素高度
     * 如都沒有，直接開始計算宿主元素高度
     * 觸發wow動畫
    */
    if (this.config.turnOn) {
      // 將套件新增原型方法addBox，來重複特效用
      (WOW.prototype as any)['addBox'] = function(e: any) {
        this.boxes.push(e);
      };

      const isImg = this.el.tagName === 'IMG';
      const childImgs = this.el.querySelectorAll('img');
      if (childImgs && childImgs.length) {
        if (this.forceInitWow) {
          this.initWow();
        } else {
          const cImgLoadStatu$s = [];
          for (let i = 0; i < childImgs.length; i++) {
            const cImg: HTMLImageElement = childImgs[i];
            if (cImg.complete) {
              cImgLoadStatu$s[i] = of(true);
            } else {
              cImgLoadStatu$s[i] = fromEvent(cImg, 'load');
            }
          }
          const subc = combineLatest(cImgLoadStatu$s).subscribe(v => {
            this.initWow();
          });
          this._subcs.push(subc);
        }
      } else if (isImg) {
        if (this.forceInitWow) {
          this.initWow();
        } else {
          if ((this.el as HTMLImageElement).complete && (this.el as HTMLImageElement).naturalHeight) {
            this.initWow();
          } else {
            this.render.listen(this.el, 'load', (evt) => {
              this.initWow();
            });
          }
        }
      } else {
        this.initWow();
      }
    }
  }

  /** 程式初始化流程 */
  private initWow() {
    /*
    * 以宿主元素傳進的屬性來構成物件
    * 以物件的鍵值跑迴圈
    * 如果此鍵有值
    * 將宿主元素加上屬性 data-wow-${attrKey}=value
    */
    const attrArray = {
      duration: this.duration ? this.duration + 's' : null,
      offset: this.offset,
      delay: this.delay ? this.delay + 's' : null,
      iteration: this.iteration,
    };
    Object.keys(attrArray).forEach((attrKey) => {
      if ((attrArray as any)[attrKey]) {
        this.el.setAttribute(`data-wow-${attrKey}`, (attrArray as any)[attrKey]);
      }
    });

    // 將元素加上class wow、傳入的效果名稱
    this.el.classList.add('wow', this.config.animate);

    if (this.replay) {
      this.scrollSubc = fromEvent(window, 'scroll').pipe(
        debounceTime(50),
        throttle(ev => interval(50))
      ).subscribe(() => this.scrollDetect(this.el));
    }

    // this.wowService.registSlide(this, this.render);
    if (this.isBrowser) {
      // Initializes the library
      this.instance = new WOW({
        boxClass: `wow-gen-${this.genCount}`,
      });
      this.instance.init();
    }
  }

  /** 計算宿主元素是否於可視範圍內 */
  private scrollDetect(element: HTMLElement) {
    const contentHeight = this.el.getBoundingClientRect().height;

    if (!this.animateEnd) {
      // 取得元件距離視窗的位置
      const objPos = element.getBoundingClientRect().top;
      // 如果 objpos 物件對於視窗上方的距離
      const invisible = objPos <= contentHeight * -1;
      // 判斷如果元件超出可視範圍上緣
      const visible = window
        .getComputedStyle(element)
        .getPropertyValue('visibility');
      // 取得元素上的visibility屬性(由wowjs加上)
      if (invisible && visible !== 'hidden' && this.applyScroll == EnumScrollType.Up) {
        /* 如果元素超出畫面上方
          * 且還未撥放動畫
          * 且設定不是下滾動觸發(上滾動或上下滾動)
          * 初始化該元素的animate狀態
          */
        this.resetAnimateCss(element);
      }
      if (objPos >= window.innerHeight && visible === 'visible' && this.applyScroll == EnumScrollType.Down) {
        /* 如果元素超出畫面下方
          * 且動畫已經撥放完
          * 且設定不是上滾動觸發(上滾動或上下滾動)
          *  初始化該元素的animate狀態
          */
        this.resetAnimateCss(element);
      }
    }
  }

  /** 重新初始化wow */
  private resetAnimateCss(element: HTMLElement) {
    // 元素清空wowjs加上的class或style
    element.classList.remove('animated');
    element.style.cssText = 'visibility: hidden; animation-name: none';
    // 呼叫addBox方法初始化新的動畫
    (this.instance as any)['addBox'](element);
    this.animateEnd = false;
  }
}

function isPluginLoaded() {
  return (window as any)['WOW'];
}

const checkPluginCanUse = (cb: () => void) => {
  const cpTmSubc = timer(0, 150).subscribe(t => {
    if (isPluginLoaded()) {
      cpTmSubc.unsubscribe();
      cb();
    }
  });
};
